import { FC, memo } from "react";
import { observer } from "mobx-react";
import { Chip, Tooltip, Typography } from "@mui/material";

import { useAppStore } from "../../../../hooks/useAppStore";
import { useChatStore } from "../../../../hooks/useChatStore";
import { APP_CONFIG_FLAGS } from "../../../../types/app";

/**
 * Runs this chat against a fake LLM instead of a real provider (REV-2161).
 *
 * Only offered where the environment enables experiments — the flag is pushed
 * over the WebSocket, so turning it off hides the control in open sessions
 * without a deploy. The server enforces the same gate on write, so hiding the
 * control is a UX decision rather than the security boundary. llm-server is
 * expected to re-check the gate before honouring the switch; until it reads
 * `experiments` at all, this only records intent (REV-2161).
 *
 * A chat that cannot take a message (closed, or someone else's) gets a quiet
 * provenance note instead of a control: changing the setting is impossible, but
 * knowing the replies were fabricated still matters when reading it back. That
 * note deliberately survives the environment flag being turned off — the
 * question it answers is "is this transcript real?", not "is the feature on?".
 *
 * ChatInput renders the read-only footer *instead of* the input form, so this
 * component is mounted in both places: the note branch only ever runs from the
 * footer, and the control branch only ever from the form.
 */
const MockLlmToggle: FC = observer(() => {
  const appStore = useAppStore();
  const chatStore = useChatStore();

  const isMocked = chatStore.isMockEnabled;
  // Until the chat's stored switches are read, `isMocked` is the environment
  // default rather than this chat's real state.
  const stateKnown = chatStore.isMockStateKnown;

  if (chatStore.isInputDisabled) {
    // A draft with no chat behind it has no replies to vouch for: ChatInput
    // keeps the form for Ask Reve (claim preview, no chat id) even though input
    // is nominally disabled, and there `isMocked` is only the environment
    // default. Saying a transcript was mocked before one exists is a lie.
    if (!stateKnown || !isMocked || !chatStore.activeChatId) return null;
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontStyle: "italic" }}
      >
        Replies in this chat came from a mocked LLM
      </Typography>
    );
  }

  const experimentsEnabled = Boolean(
    appStore?.configFlags?.[APP_CONFIG_FLAGS.CHAT_EXPERIMENTS_ENABLED],
  );
  if (!experimentsEnabled) return null;

  // Chip's `disabled` only stops clicks through CSS pointer-events, so guard the
  // handler too rather than trusting styling to enforce it.
  const inert = !stateKnown;

  return (
    <Tooltip
      title={
        isMocked
          ? "Replies come from a fake LLM — no real model is called. Click to use the live agent."
          : "Replies come from the live agent and cost a real LLM call. Click to use the mock."
      }
    >
      <Chip
        label={isMocked ? "Mock LLM" : "Live agent"}
        size="small"
        aria-label="Toggle mocked LLM for this chat"
        aria-pressed={isMocked}
        color={isMocked ? "warning" : "default"}
        variant={isMocked ? "filled" : "outlined"}
        onClick={() => {
          if (inert) return;
          chatStore.toggleMock();
        }}
        disabled={inert}
        sx={{ cursor: inert ? "default" : "pointer", fontWeight: 500 }}
      />
    </Tooltip>
  );
});

export default memo(MockLlmToggle);
